import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  // Chip,
  CircularProgress,
  Alert,
  IconButton,
  Stack
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import PositionsApi from '../../services/positionsApi';
import PageContainer from '../layout/PageContainer';

const CareersPage = () => {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PositionsApi.getPublicPositions({ limit: 20, offset: 0 });
      if (response.success) setPositions(response.data);
      else setError('Failed to fetch positions');
    } catch (err) {
      console.error(err);
      setError('Failed to load positions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToPositions = () => {
    document.getElementById('open-positions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const hiringSteps = [
    { number: '01', title: 'Application and recruiter screen', description: 'Once you apply, our recruitment team reviews your background, skills, and experience to ensure they align with the role. If your profile looks like a good fit, we’ll schedule a quick call to learn more about your interests and career goals.' },
    { number: '02', title: 'First round interview', description: 'You’ll meet with a hiring manager or team lead to discuss your experience in more detail. This conversation helps us understand how you think, solve problems, and collaborate with others.' },
    { number: '03', title: 'Task or challenge', description: 'You’ll receive a short, role-related task designed to reflect real scenarios we work on.This helps us understand your problem-solving skills, creativity, and attention to detail.Take your time to complete it - we value your approach as much as the final result.' },
    { number: '04', title: 'Final interview', description: 'You’ll meet key team members or leadership to talk through your results, expectations, and fit within our culture. If everything aligns, we’ll move forward with an offer to welcome you aboard!' },
  ];

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#8ABEFC', mr: 2 }} />
        <Typography sx={{ color: '#fff', fontFamily: '"Roboto", sans-serif' }}>
          Loading positions...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#000', color: '#fff' }}>
      <PageContainer>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', py: { xs: 6, md: 10 } }}>
          <Typography sx={{ fontSize: { xs: '24px', md: '53px' }, fontWeight: 700, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
            Make an impact at front
          </Typography>
          <Typography sx={{ fontSize: { xs: '14px', md: '24px' }, color: 'rgba(147,147,152,1)', mb: 3 }}>
            Ready to take role where you can make a lasting impact on your team,<br />We'd love to have more talented people on board
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={scrollToPositions}
              sx={{ backgroundColor: '#fff', color: '#000', px: 4, py: 1.5, fontWeight: 600, '&:hover': { backgroundColor: '#e0e0e0' } }}
            >
              OPEN POSITIONS
            </Button>
            <Button
              onClick={scrollToPositions}
              sx={{ backgroundColor: '#fff', color: '#000', p: 1.5, '&:hover': { backgroundColor: '#e0e0e0' } }}
            >
              <ChevronDown size={24} />
            </Button>
          </Box>
        </Box>

        {/* How We Hire Section */}
        <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
          <Typography sx={{ color: 'rgba(147,147,152,1)', fontWeight: 'bold', fontSize: { xs: '12px', md: '20px' }, letterSpacing: 2, mb: 1 }}>
            HOW WE HIRE OUR TEAM
          </Typography>
          <Typography sx={{ fontSize: { xs: '18px', md: '32px' }, fontWeight: 400, maxWidth: 900, mx: 'auto', mb: 4 }}>
            We like to keep things as simple as possible so we can get to <br />what's really important - finding out more about you
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {hiringSteps.map((step, i) => {
              const cardHeights = [400, 410, 440, 380];
              const bgColors = ['#A4A6A7', '#C2C5C5', '#DBDFDF', '#FFFEFE'];
              return (
                <Grid item key={i}>
                  <Box
                    sx={{
                      width: '286px',
                      height: `${cardHeights[i]}px`,
                      backgroundColor: bgColors[i],
                      borderRadius: 0,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ color: '#000', fontSize: '35px', fontWeight: 500, mb: 1 }}>
                      {step.number}
                    </Typography>
                    <Typography sx={{ color: '#000', fontSize: '24px', fontWeight: 700, mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: '#000', fontSize: '20px', fontWeight: 400, lineHeight: '24px' }}>
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Updated Open Positions Section (Anima Style) */}
        <Box id="open-positions" sx={{ py: { xs: 4, md: 8 }, textAlign: 'left' }}>
          <Typography
            sx={{ textAlign: 'left', color: 'rgba(147,147,152,1)', fontWeight: 'bold', fontSize: { xs: '12px', md: '20px' }, letterSpacing: 2, mb: 1 }}>
            OPEN POSITIONS
          </Typography>

          <Typography
           sx={{ fontSize: { xs: '18px', md: '32px' }, fontWeight: 400, maxWidth: 1200, mx: 'auto', mb: 4 }}>
          
            Get to know us and join our team. We'd love to have more talented people on board. Check out our open positions!
          </Typography>

          {error && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              <Button onClick={fetchPositions} sx={{ color: '#8ABEFC', borderColor: '#8ABEFC', border: '1px solid' }}>Try Again</Button>
            </Box>
          )}

          {/* Position Cards */}
          <Stack
            direction="column"
            spacing={4}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            {positions.length > 0 ? (
              positions.map((pos) => (
                <Stack
                  key={pos.id}
                  direction="row"
                  sx={{
                    width: { xs: '90%', sm: '589px' },
                    height: '104px',
                    backgroundColor: 'white',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 4,
                    borderRadius: '12px',
                  }}
                >
                  <Typography
                    sx={{ fontSize: { color: 'black',xs: '18px', md: '32px' }, fontWeight: 400, maxWidth: 1200, mx: 'auto' }}
                  >
                    {pos.title}
                  </Typography>

                  <IconButton
                    onClick={() => navigate(`/application?positionId=${pos.id}`)}
                    sx={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: 'black',
                      borderRadius: '50%',
                      '&:hover': { backgroundColor: '#333' },
                    }}
                  >
                    <ArrowForwardIcon sx={{ color: 'white', fontSize: '28px' }} />
                  </IconButton>
                </Stack>
              ))
            ) : (
              <Typography sx={{ color: '#ccc' }}>No positions available at the moment.</Typography>
            )}
          </Stack>
        </Box>
      </PageContainer>
    </Box>
  );
};

export default CareersPage;
